import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDay, formatPrice } from '@/lib/format-utils';
import { Tooltip, Badge, TextInput, Select } from '@mantine/core';
import { Payment } from '@turizi/shared';
import { getPaymentAppliedAmount, getPaymentServiceBookingAppliedAmount } from '@/lib/models-utils';
import { Link } from 'react-router-dom';
import { PAYMENTS_PATH, RESERVATIONS_PATH } from '@/lib/navigation';
import {
    IconArrowDownLeft,
    IconArrowsExchange,
    IconArrowUpRight,
    IconAlertCircle,
    IconClock,
    IconCircleCheckFilled,
    IconLinkOff,
    IconSearch,
    IconX
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { TagIcon } from '@/lib/tag-utils';
import { localTenant } from '@/services/session-service';
import { PaymentMethodBadge } from './snippets';
import SearchInput from './search-input';


interface Props {
    payments: Payment[];
    title?: string;
    ownedBusinessIds?: number[];
    showHeader?: boolean;
    showFilters?: boolean;
}

export type PaymentLinkStatus = 'not_linked' | 'partially_linked' | 'fully_linked' | 'over_allocated';

export const getPaymentAllocated = (payment: Payment): number => {
    let allocated = 0;

    if (Array.isArray(payment.affected_reservations) && payment.affected_reservations.length > 0) {
        payment.affected_reservations.forEach((item: any) => {
            if (typeof item === 'object' && item !== null && item.amount !== undefined) {
                allocated += Number(item.amount || 0);
            }
        });
    } else if (Array.isArray(payment.reservations) && payment.reservations.length > 0) {
        payment.reservations.forEach((r: any) => {
            const amt = r.PaymentReservation?.amount ?? r.payment_reservation?.amount;
            if (amt !== undefined && amt !== null) {
                allocated += Number(amt);
            }
        });
    }

    if (Array.isArray(payment.affected_service_bookings) && payment.affected_service_bookings.length > 0) {
        payment.affected_service_bookings.forEach((item: any) => {
            if (typeof item === 'object' && item !== null && item.amount !== undefined) {
                allocated += Number(item.amount || 0);
            }
        });
    } else if (Array.isArray(payment.service_bookings) && payment.service_bookings.length > 0) {
        payment.service_bookings.forEach((sb: any) => {
            const amt = sb.PaymentServiceBooking?.amount ?? sb.payment_service_booking?.amount;
            if (amt !== undefined && amt !== null) {
                allocated += Number(amt);
            }
        });
    }

    return allocated;
};

export const getPaymentLinkStatus = (payment: Payment): {
    linkStatus: PaymentLinkStatus;
    allocated: number;
    unallocated: number;
    netAmt: number;
    hasLinkedItems: boolean;
} => {
    const totalAmt = Number(payment.status === 'pending' ? (payment.expected_amount || 0) : (payment.amount || 0));
    const refund = Number(payment.refunded_amount || 0);
    const netAmt = Math.max(0, totalAmt - refund);
    const allocated = getPaymentAllocated(payment);
    const unallocated = netAmt - allocated;

    const hasLinkedItems = (payment.reservations && payment.reservations.length > 0) ||
        (payment.affected_reservations && payment.affected_reservations.length > 0) ||
        (payment.service_bookings && payment.service_bookings.length > 0) ||
        (payment.affected_service_bookings && payment.affected_service_bookings.length > 0);

    let linkStatus: PaymentLinkStatus;
    if (!hasLinkedItems || allocated <= 0.001) {
        linkStatus = 'not_linked';
    } else if (allocated > netAmt + 0.01) {
        linkStatus = 'over_allocated';
    } else if (unallocated > 0.01) {
        linkStatus = 'partially_linked';
    } else {
        linkStatus = 'fully_linked';
    }

    return { linkStatus, allocated, unallocated, netAmt, hasLinkedItems };
};

export default function PaymentsTable({
    payments,
    title,
    showHeader = true,
    showFilters = true
}: Props): JSX.Element {
    const { t } = useTranslation();
    const ownedBusinessIds = localTenant()?.businesses?.map(b => b.id) || [];

    const [searchQuery, setSearchQuery] = useState('');
    const [assignmentFilter, setAssignmentFilter] = useState<'all' | 'fully' | 'partial' | 'none'>('all');

    const hasIssues = (payment: Payment) => {
        const isPending = payment.status === 'pending';
        const amountToCheck = isPending ? (payment.expected_amount || 0) : (payment.amount || 0);
        const isAmountValid = Number(amountToCheck) > 0;

        const isPayerPayeeDifferent =
            !(payment.from_business_id && payment.from_business_id === payment.to_business_id);

        return !isAmountValid || !isPayerPayeeDifferent;
    };

    const displayedPayments = useMemo(() => {
        const query = searchQuery.trim().toLowerCase().replace(/^#/, '');

        return payments
            .filter((payment) => {
                const info = getPaymentLinkStatus(payment);

                // Assignment filter
                if (assignmentFilter === 'fully' && info.linkStatus !== 'fully_linked') {
                    return false;
                }
                if (assignmentFilter === 'partial' && info.linkStatus !== 'partially_linked') {
                    return false;
                }
                if (assignmentFilter === 'none' && info.linkStatus !== 'not_linked') {
                    return false;
                }

                // Search query filter: ID, payer, payee, amount, notes
                if (query) {
                    const idMatch = payment.id?.toString().includes(query);
                    const payerMatch = payment.from_name?.toLowerCase().includes(query);
                    const payeeMatch = payment.to_name?.toLowerCase().includes(query);
                    const amountStr = payment.amount !== undefined && payment.amount !== null ? payment.amount.toString() : '';
                    const expectedAmountStr = payment.expected_amount !== undefined && payment.expected_amount !== null ? payment.expected_amount.toString() : '';
                    const formattedAmount = formatPrice(payment.amount).toLowerCase();
                    const amountMatch = amountStr.includes(query) || expectedAmountStr.includes(query) || formattedAmount.includes(query);
                    const creatorMatch = payment.created_by?.name?.toLowerCase().includes(query) || payment.created_by?.email?.toLowerCase().includes(query);

                    if (!idMatch && !payerMatch && !payeeMatch && !amountMatch && !creatorMatch) {
                        return false;
                    }
                }

                return true;
            })
            .sort((a, b) => {
                const dateA = a.payment_date || a.due_date || '';
                const dateB = b.payment_date || b.due_date || '';
                return dateB.localeCompare(dateA);
            });
    }, [payments, searchQuery, assignmentFilter]);

    return (
        <div>
            {title && (
                <div className="text-lg font-semibold mb-2">
                    {title}
                </div>
            )}

            {showFilters && (
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mb-6">
                    <div className="flex flex-1 items-center gap-2 max-w-lg">
                        <SearchInput
                            initialValue={searchQuery}
                            onSearch={setSearchQuery}
                        />
                        {/* <TextInput
                            placeholder={t('search_payments_placeholder')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.currentTarget.value)}
                            leftSection={<IconSearch size={15} className="text-gray-400" />}
                            rightSection={
                                searchQuery ? (
                                    <button
                                        type="button"
                                        onClick={() => setSearchQuery('')}
                                        className="text-gray-400 hover:text-gray-600 cursor-pointer p-0.5"
                                    >
                                        <IconX size={14} />
                                    </button>
                                ) : null
                            }
                            size="xs"
                            className="flex-1"
                        /> */}
                        {/* not usefull for now */}
                        {/* <Select
                            value={assignmentFilter}
                            onChange={(val) => setAssignmentFilter((val as any) || 'all')}
                            data={[
                                { value: 'all', label: t('all') },
                                { value: 'fully', label: t('fully_linked') },
                                { value: 'partial', label: t('partially_linked') },
                                { value: 'none', label: t('not_linked') }
                            ]}
                            size="xs"
                            className="w-48"
                        /> */}
                    </div>
                    <div className="text-xs text-gray-500 font-medium whitespace-nowrap self-end sm:self-center">
                        {t('showing_results', { count: displayedPayments.length, total: payments.length })}
                    </div>
                </div>
            )}

            <div className="subtle-table">
                <table>
                    {showHeader && (
                        <thead>
                            <tr>
                                <th>#id</th>
                                <th>{t('dates')}</th>
                                <th>{t('origin')}</th>
                                <th>{t('recipient')}</th>
                                <th>
                                    <div className="flex justify-end">
                                        {t('amount')}
                                    </div>
                                </th>
                                <th>
                                    {t('allocation_status')}
                                </th>
                                <th>
                                    {t('linked_items')}
                                </th>
                                <th>
                                    {t('notes')}
                                </th>
                                <th>
                                    <div className="flex justify-end">
                                        {t('payment_method')}
                                    </div>
                                </th>
                            </tr>
                        </thead>
                    )}
                    <tbody>
                        {displayedPayments.length === 0 ? (
                            <tr>
                                <td colSpan={9} className="text-center py-8 text-gray-400 text-xs">
                                    {t('no_payments_found')}
                                </td>
                            </tr>
                        ) : (
                            displayedPayments.map((payment) => {
                                const isMoneyIn = ownedBusinessIds.includes(payment.to_business_id || -1);
                                const isMoneyOut = ownedBusinessIds.includes(payment.from_business_id || -1);
                                const isOverdue = !payment.payment_date && payment.due_date && dayjs(payment.due_date).isBefore(dayjs(), 'day');
                                const isPending = payment.status === 'pending';

                                let flowIcon = null;
                                if (isMoneyIn && isMoneyOut) {
                                    flowIcon = (
                                        <Tooltip label={t('transfer')} position="right" withArrow>
                                            <div className="flex items-center gap-1 justify-center text-blue-500">
                                                {isPending ? <IconClock color='orange' size={14} /> : <IconCircleCheckFilled color='#ddd' size={14} />}
                                                <IconArrowsExchange size={14} stroke={1} />
                                            </div>
                                        </Tooltip>
                                    );
                                } else if (isMoneyIn) {
                                    flowIcon = (
                                        <Tooltip label={t('money_in')} position="right" withArrow>
                                            <div className="flex items-center gap-1 justify-center text-emerald-800">
                                                {isPending ? <IconClock color='orange' size={14} /> : <IconCircleCheckFilled color='#ddd' size={14} />}
                                                <IconArrowDownLeft size={14} stroke={1} />
                                            </div>
                                        </Tooltip>
                                    );
                                } else if (isMoneyOut) {
                                    flowIcon = (
                                        <Tooltip label={t('money_out')} position="right" withArrow>
                                            <div className="flex items-center gap-1 justify-center text-rose-500">
                                                {isPending ? <IconClock color='orange' size={14} /> : <IconCircleCheckFilled color='#ddd' size={14} />}
                                                <IconArrowUpRight size={18} stroke={1} />
                                            </div>
                                        </Tooltip>
                                    );
                                }

                                const hasRowIssues = hasIssues(payment);
                                const linkInfo = getPaymentLinkStatus(payment);

                                return (
                                    <tr className={`clickable-row ${hasRowIssues ? 'bg-red-50 hover:bg-red-100/80 text-red-950 border-l-4 border-red-500' : 'hover:bg-gray-50/80'}`} key={payment.id}>
                                        <td className="align-middle py-3 px-2">
                                            <div className="flex items-center gap-1.5">
                                                {hasRowIssues ? (
                                                    <Tooltip label={t('payment_issue_warning')} position="top" withArrow>
                                                        <div className="flex justify-center text-red-800 animate-pulse">
                                                            <IconAlertCircle size={16} stroke={2} />
                                                        </div>
                                                    </Tooltip>
                                                ) : (
                                                    flowIcon
                                                )}
                                                <Link
                                                    to={`${PAYMENTS_PATH}/${payment.id}`}
                                                    className="main-link text-xs font-light"
                                                    style={{
                                                        color: 'blue'
                                                    }}
                                                >
                                                    #{payment.id}
                                                </Link>
                                            </div>
                                        </td>
                                        <td className="align-middle py-3 px-2">
                                            <div className="flex flex-col gap-0.5">
                                                {payment.payment_date ? (
                                                    <span className="text-gray-900 font-medium text-xs" title={t('payment_date')}>
                                                        {formatDay(payment.payment_date, '')}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-400 italic text-xs">
                                                        {t('no_payment_date')}
                                                    </span>
                                                )}
                                                {payment.created_at && (
                                                    <span className="text-[10px] text-gray-400" title={`${t('registration_date')}: ${dayjs(payment.created_at).format('DD MMM YYYY HH:mm')}`}>
                                                        {t('registered')}: {dayjs(payment.created_at).format('DD MMM YYYY')}
                                                    </span>
                                                )}
                                                {payment.due_date && !payment.payment_date && (
                                                    <span className={`text-[10px] ${isOverdue ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
                                                        {t('due')}: {formatDay(payment.due_date, '')}
                                                    </span>
                                                )}
                                                {payment.created_by?.name && (
                                                    <span className="text-[10px] text-gray-400 truncate max-w-[120px]" title={`${t('created_by')}: ${payment.created_by.name}`}>
                                                        {payment.created_by.name}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="align-middle text-gray-800 text-sm py-3 px-2">
                                            <div className="flex items-center gap-1">
                                                {payment.from_business_id && (
                                                    <TagIcon name={payment.from_business_category || 'business'} size="xxs" hideText={true} noBackground={true} />
                                                )}
                                                <span className={ownedBusinessIds.includes(payment.from_business_id || -1) ? 'font-normal text-gray-400' : 'font-semibold text-gray-800'}>
                                                    {payment.from_name || <span className="text-gray-400 font-normal italic">{t('none')}</span>}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="align-middle text-gray-800 text-sm py-3 px-2">
                                            <div className="flex items-center gap-1">
                                                {payment.to_business_id && (
                                                    <TagIcon name={payment.to_business_category || 'business'} size="xxs" hideText={true} noBackground={true} />
                                                )}
                                                <span className={ownedBusinessIds.includes(payment.to_business_id || -1) ? 'font-normal text-gray-400' : 'font-semibold text-gray-800'}>
                                                    {payment.to_name || <span className="text-gray-400 font-normal italic">{t('none')}</span>}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="align-middle number py-3 px-2">
                                            <div className="flex flex-col items-end">
                                                <div className="flex justify-end items-baseline font-bold text-sm">
                                                    <span className={
                                                        (isMoneyIn && isMoneyOut)
                                                            ? 'text-gray-800'
                                                            : isMoneyIn
                                                                ? 'text-emerald-800'
                                                                : isMoneyOut
                                                                    ? 'text-rose-800'
                                                                    : 'text-gray-800'
                                                    }>
                                                        <span className="text-gray-400 mr-0.5 font-normal">$</span>
                                                        {formatPrice(payment.amount)}
                                                    </span>
                                                    <span className="text-[10px] text-gray-400 font-semibold ml-1">{payment.currency}</span>
                                                </div>
                                                {payment.status === 'pending' && payment.expected_amount !== null && Number(payment.expected_amount) > 0 && (
                                                    <div className="text-[10px] text-gray-400 font-semibold mt-0.5">
                                                        {formatPrice(payment.expected_amount)} {payment.currency}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="align-middle py-3 px-2">
                                            {linkInfo.linkStatus === 'partially_linked' && (
                                                <div className="flex flex-col gap-1 items-start">
                                                    <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200/60 px-1.5 py-0.5 rounded whitespace-nowrap">
                                                        {t('unallocated_balance')}: ${formatPrice(linkInfo.unallocated)} {payment.currency}
                                                    </span>
                                                </div>
                                            )}
                                            {linkInfo.linkStatus === 'not_linked' && (
                                                <Badge size="xs" color="gray" variant="light" className="font-medium gap-1 text-slate-500 bg-slate-100 border border-slate-200">
                                                    <IconLinkOff size={11} className="shrink-0 text-slate-400" />
                                                </Badge>
                                            )}
                                            {linkInfo.linkStatus === 'over_allocated' && (
                                                <Badge size="xs" color="red" variant="light" className="font-bold gap-1">
                                                    <IconAlertCircle size={11} className="shrink-0" />
                                                </Badge>
                                            )}
                                        </td>
                                        <td className="align-middle py-3 px-2">
                                            {linkInfo.hasLinkedItems ? (
                                                <div className="flex gap-1 flex-wrap items-center max-w-xs">
                                                    {payment?.reservations?.map((r: any) => {
                                                        const assignedAmt = getPaymentAppliedAmount(payment, r.id);

                                                        return (
                                                            <Link
                                                                key={r.id}
                                                                to={`${RESERVATIONS_PATH}/${r.id}`}
                                                                className="px-2 py-0.5 rounded bg-gray-50 text-gray-700 hover:bg-blue-50 hover:text-blue-800 text-xs font-medium border border-gray-200/60 shadow-3xs inline-flex items-center gap-1"
                                                            >
                                                                <span className="font-bold">#{r.id}</span>
                                                                {assignedAmt > 0 && (
                                                                    <span className="font-semibold text-emerald-700">
                                                                        (${formatPrice(Number(assignedAmt))})
                                                                    </span>
                                                                )}
                                                            </Link>
                                                        );
                                                    })}
                                                    {payment?.service_bookings?.map((b: any) => {
                                                        const bkAssignedAmt = getPaymentServiceBookingAppliedAmount(payment, b.id);

                                                        return (
                                                            <Tooltip
                                                                key={b.id}
                                                                label={`
                                                                    ${b.reservation_id ? t('reservation') + ': ' + b.reservation_id : ''}
                                                                    ${b.service_order_id ? t('service_order') + ': ' + b.service_order_id : ''}
                                                                `}
                                                                position="top"
                                                                withArrow
                                                            >
                                                                <Link
                                                                    to={b.reservation_id ? `${RESERVATIONS_PATH}/${b.reservation_id}` : '#'}
                                                                    className="px-2 py-0.5 rounded bg-gray-50 text-gray-700 hover:bg-blue-50 hover:text-blue-800 text-xs font-medium border border-gray-200/60 shadow-3xs inline-flex items-center gap-1"
                                                                >
                                                                    <span>
                                                                        {b.reservation_id ? `#${b.reservation_id}:${b.id}` : ''}
                                                                        {b.service_order_id ? `#order-${b.service_order_id}:${b.id}` : ''}
                                                                    </span>
                                                                    {bkAssignedAmt != null && (
                                                                        <span className="font-semibold text-emerald-700">
                                                                            (${formatPrice(Number(bkAssignedAmt))})
                                                                        </span>
                                                                    )}
                                                                </Link>
                                                            </Tooltip>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <span className="text-gray-300 font-light text-xs">-</span>
                                            )}
                                        </td>
                                        <td className="align-middle py-3 px-2">
                                            <div className="text-xs text-gray-600 max-w-[150px] truncate" title={payment.notes || ''}>
                                                {payment.notes || <span className="text-gray-300 font-light">-</span>}
                                            </div>
                                        </td>
                                        <td className="align-middle py-3 px-2">
                                            <div className="flex justify-end">
                                                <PaymentMethodBadge method={payment.payment_method} />
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
